import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@bb/ui';
import { ApiError, type AdminApi, type CommentObjectType } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { MentionInput } from './MentionInput.tsx';
import { ThreadView } from './ThreadView.tsx';

const WRITE: Record<CommentObjectType, string[]> = { campaign: ['campaigns.edit', 'messages.approve'], canvas: ['canvas.edit', 'messages.approve'] };

/**
 * Comments of a campaign or Canvas (BB-21): threads filtered by open / resolved, a new thread on the object or on a
 * Canvas step, replies with mentions. Editors and reviewers write; everyone with access reads.
 */
export function CommentsSidebar({ environmentId, objectType, objectId, steps = [] }: { environmentId: string; objectType: CommentObjectType; objectId: string; steps?: Array<{ id: string; name: string }> }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canWrite = WRITE[objectType].some((p) => permissions.has(p));
  const [status, setStatus] = useState<'open' | 'resolved' | 'all'>('open');
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [stepId, setStepId] = useState('');
  const key = ['comments', environmentId, objectType, objectId];
  const threads = useQuery({ queryKey: [...key, status], queryFn: () => api.commentThreads(environmentId, objectType, objectId, status) });
  const create = useMutation({
    mutationFn: () => api.createThread(environmentId, objectType, objectId, { body: body.trim(), mentions, anchor: stepId ? { stepId } : null }),
    onSuccess: () => {
      setBody('');
      setMentions([]);
      setStepId('');
      return queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const stepName = (id: string | undefined) => steps.find((s) => s.id === id)?.name ?? id;
  const open = threads.data ? threads.data.openOnObject + Object.values(threads.data.openByStep).reduce((a, b) => a + b, 0) : 0;
  return (
    <aside aria-label="Comments" className="panel comments">
      <h2>
        Comments <span className="hint">{open} open</span>
      </h2>
      <select aria-label="Show comments" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
        <option value="open">Open</option>
        <option value="resolved">Resolved</option>
        <option value="all">All</option>
      </select>
      {threads.data?.items.length === 0 && <p className="hint">{status === 'open' ? 'No open comments.' : 'No comments.'}</p>}
      {threads.data?.items.map((t) => <ThreadView key={t.id} environmentId={environmentId} thread={t} stepName={t.anchor ? stepName(t.anchor.stepId) : undefined} canWrite={canWrite} />)}
      {canWrite && (
        <div className="comments__new">
          {objectType === 'canvas' && steps.length > 0 && (
            <select aria-label="Comment on" value={stepId} onChange={(e) => setStepId(e.target.value)}>
              <option value="">The whole Canvas</option>
              {steps.map((s) => (
                <option key={s.id} value={s.id}>
                  Step: {s.name}
                </option>
              ))}
            </select>
          )}
          <MentionInput
            environmentId={environmentId}
            label="New comment"
            value={body}
            mentions={mentions}
            onChange={(value, next) => {
              setBody(value);
              setMentions(next);
            }}
          />
          <Button variant="primary" disabled={!body.trim()} loading={create.isPending} onClick={() => create.mutate()}>
            Comment
          </Button>
          {create.isError && <p role="alert">{create.error instanceof ApiError ? create.error.message : 'Something went wrong.'}</p>}
        </div>
      )}
    </aside>
  );
}

const NO_COUNTS: Record<string, number> = Object.freeze({}) as Record<string, number>;

/** Open threads per step, for node counters (graph editor, BB-114) and the step list. */
export function useStepCommentCounts(environmentId: string, canvasId: string): Record<string, number> {
  const { api } = useRouter().options.context as { api: AdminApi };
  const threads = useQuery({ queryKey: ['comments', environmentId, 'canvas', canvasId, 'open'], queryFn: () => api.commentThreads(environmentId, 'canvas', canvasId, 'open') });
  return threads.data?.openByStep ?? NO_COUNTS;
}
